import api from '@/api/axiosApi'
import { mapApiPlanToFrontend } from '@/api/assortmentPlans'
import type {
  PaginatedBrowseDivisionsResponse,
  PaginatedBrowseDepartmentsResponse,
  PaginatedBrowseCalendarsResponse,
  PaginatedBrowseCalendarEventsResponse,
  PaginatedPlansResponse,
  AssortmentPlan,
  PaginationMeta,
} from '@/types'

interface BrowseHierarchyParams {
  offset?: number
  limit?: number
  q?: string
}

// ─── Fetch divisions that have browsable departments ────────────

export async function fetchBrowseDivisions(
  params: BrowseHierarchyParams = {}
): Promise<PaginatedBrowseDivisionsResponse> {
  const response = await api.get<PaginatedBrowseDivisionsResponse>(
    '/plans/browse/divisions',
    {
      params: {
        offset: params.offset ?? 0,
        limit: params.limit ?? 50,
        q: params.q || undefined,
      },
    }
  )
  return response.data
}

// ─── Fetch departments for a division (including unassigned) ────

export async function fetchBrowseDepartments(
  divisionId: string,
  params: BrowseHierarchyParams = {}
): Promise<PaginatedBrowseDepartmentsResponse> {
  const response = await api.get<PaginatedBrowseDepartmentsResponse>(
    `/plans/browse/divisions/${divisionId}/departments`,
    {
      params: {
        offset: params.offset ?? 0,
        limit: params.limit ?? 50,
        q: params.q || undefined,
      },
    }
  )
  return response.data
}

// ─── Fetch calendars for a department ───────────────────────────

export async function fetchBrowseCalendars(
  departmentId: string,
  params?: { offset?: number; limit?: number }
): Promise<PaginatedBrowseCalendarsResponse> {
  const response = await api.get<PaginatedBrowseCalendarsResponse>(
    `/plans/browse/departments/${departmentId}/calendars`,
    { params }
  )
  return response.data
}

// ─── Fetch events for a seasonal calendar ───────────────────────

export async function fetchBrowseCalendarEvents(
  departmentId: string,
  calendarId: string,
  params?: { offset?: number; limit?: number }
): Promise<PaginatedBrowseCalendarEventsResponse> {
  const response = await api.get<PaginatedBrowseCalendarEventsResponse>(
    `/plans/browse/departments/${departmentId}/calendars/${calendarId}/events`,
    { params }
  )
  return response.data
}

// ─── Fetch plans for a department (optionally filtered) ─────────

export interface BrowsePlansParams {
  departmentId: string
  calendarId?: string | null
  isSeasonal?: boolean
  calendarEventId?: string | null
  noCalendar?: boolean
  page?: number
  pageSize?: number
}

interface BrowsePlansResult {
  plans: AssortmentPlan[]
  totalCount: number
  totalPages: number
  currentPage: number
  pagination: PaginationMeta
}

export async function fetchBrowsePlans(
  filters: BrowsePlansParams
): Promise<BrowsePlansResult> {
  const {
    departmentId,
    calendarId,
    isSeasonal,
    calendarEventId,
    noCalendar,
    page = 1,
    pageSize = 20,
  } = filters

  const offset = (page - 1) * pageSize

  const params: Record<string, string | number | boolean> = {
    offset,
    limit: pageSize,
    sort: '-modified_at',
  }

  if (calendarId) params.calendar_id = calendarId
  if (isSeasonal != null) params.is_seasonal = isSeasonal
  if (calendarEventId) params.calendar_event_id = calendarEventId
  if (noCalendar) params.no_calendar = true

  const response = await api.get<PaginatedPlansResponse>(
    `/plans/browse/departments/${departmentId}/plans`,
    { params }
  )

  const { data: items, pagination } = response.data
  const plans = items.map(mapApiPlanToFrontend)
  const totalCount = pagination.total
  const totalPages = Math.ceil(totalCount / pageSize)

  return { plans, totalCount, totalPages, currentPage: page, pagination }
}
